import { ownTimerReviewSchema, type OwnTimerReview } from './ownTimerReviewContract';
import { ownTimeReviewSchema, type OwnTimeReview } from './ownTimeReviewContract';
import { workExecutionReviewSchema, type WorkExecutionReview } from './workExecutionReviewContract';

import workReviewPresenters from './workReviewPresenters.json';

const WORK_TOOLS: Record<string, string> = workReviewPresenters;
const OWN_KEYS: Record<string, 'create' | 'update' | 'delete'> = {
  'confirmation.presenters.createOwnTimeEntry': 'create',
  'confirmation.presenters.updateOwnTimeEntry': 'update',
  'confirmation.presenters.deleteOwnTimeEntry': 'delete',
};
const EXECUTION_KEYS: Record<string, 'start' | 'pause' | 'resume'> = {
  'confirmation.presenters.start_work_order_work': 'start',
  'confirmation.presenters.pause_work_order_work': 'pause',
  'confirmation.presenters.resume_work_order_work': 'resume',
};
export type ParsedWorkReview =
  | { kind: 'absent' }
  | { kind: 'invalid' }
  | { kind: 'timer'; review: OwnTimerReview }
  | { kind: 'ownTime'; review: OwnTimeReview }
  | {
      kind: 'execution';
      review: WorkExecutionReview;
      /**
       * The workshop zone the staff chat resolved the continuation in
       * (`workshopTimezone` beside the review, #3479 lane 3). Absent: the card
       * shows the raw instant; the browser never guesses a zone.
       */
      timeZone?: string;
    };

/** A missing, malformed or mismatched review must never become a headline-only affirm. */
export function parseWorkReview(
  params: Record<string, unknown>,
  key?: string,
  toolName?: string
): ParsedWorkReview {
  const requiredKey = Object.hasOwn(WORK_TOOLS, toolName ?? '')
    ? WORK_TOOLS[toolName ?? '']
    : undefined;
  if (requiredKey && key !== requiredKey) return { kind: 'invalid' };
  const own = 'ownTimeReview' in params;
  const execution = 'workExecutionReview' in params;
  const timer = 'ownTimerReview' in params;
  if ([own, execution, timer].filter(Boolean).length > 1) return { kind: 'invalid' };
  const ownAction = Object.hasOwn(OWN_KEYS, key ?? '') ? OWN_KEYS[key ?? ''] : undefined;
  const executionAction = Object.hasOwn(EXECUTION_KEYS, key ?? '')
    ? EXECUTION_KEYS[key ?? '']
    : undefined;
  const timerAction = key === 'confirmation.presenters.startOwnWorkOrderTimer' ? 'start' : key === 'confirmation.presenters.stopOwnWorkOrderTimer' ? 'stop' : undefined;
  if (timer) {
    const parsed = ownTimerReviewSchema.safeParse(params.ownTimerReview);
    if (!parsed.success || !timerAction || parsed.data.command.action !== timerAction) return { kind: 'invalid' };
    return { kind: 'timer', review: parsed.data };
  }
  if (!own && !execution)
    return ownAction || executionAction || timerAction ? { kind: 'invalid' } : { kind: 'absent' };
  if (own) {
    const parsed = ownTimeReviewSchema.safeParse(params.ownTimeReview);
    if (!parsed.success || !ownAction || parsed.data.command.action !== ownAction)
      return { kind: 'invalid' };
    const { before, after } = parsed.data.effects;
    if (
      (ownAction === 'create' && (before !== null || after === null)) ||
      (ownAction === 'delete' && (before === null || after !== null)) ||
      (ownAction === 'update' && (before === null || after === null))
    )
      return { kind: 'invalid' };
    return { kind: 'ownTime', review: parsed.data };
  }
  const parsed = workExecutionReviewSchema.safeParse(params.workExecutionReview);
  if (
    !parsed.success ||
    !executionAction ||
    parsed.data.command.action !== executionAction ||
    parsed.data.command.workOrderId !== parsed.data.effects.workOrderId ||
    parsed.data.effects.statusAfter !== (executionAction === 'pause' ? 'paused' : 'in_progress')
  ) {
    return { kind: 'invalid' };
  }
  const effects = parsed.data.effects;
  if (
    (effects.assignedMechanicIdBefore === null && effects.assignedToCallerBefore) ||
    (effects.assignedMechanicIdAfter === null && effects.assignedToCallerAfter) ||
    (effects.assignedMechanicIdBefore === effects.assignedMechanicIdAfter &&
      effects.assignedToCallerBefore !== effects.assignedToCallerAfter) ||
    (effects.assignedToCallerBefore &&
      effects.assignedToCallerAfter &&
      effects.assignedMechanicIdBefore !== effects.assignedMechanicIdAfter) ||
    ((effects.timer.effect === 'open' || effects.timer.effect === 'none') &&
      effects.timer.entryId !== null) ||
    ((effects.timer.effect === 'retain' || effects.timer.effect === 'close') &&
      effects.timer.entryId === null)
  )
    return { kind: 'invalid' };
  const zone = params.workshopTimezone;
  return {
    kind: 'execution',
    review: parsed.data,
    // An unknown or blank name formats nothing (`Intl` refuses it): raw instant.
    ...(typeof zone === 'string' ? { timeZone: zone } : {}),
  };
}
